import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/DateRangePicker";
import { FileText, FileSpreadsheet, FileDown, Loader2 } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { toast } from "sonner";

const TYPES = [
  { k: "sales", l: "Sales Summary" },
  { k: "gst", l: "GST Sales Summary" },
  { k: "tax_summary", l: "Tax Summary Report" },
  { k: "gstr", l: "GST Register (GSTR-3B)" },
  { k: "credit", l: "Member Directory & Outstanding Report" },
  { k: "inventory", l: "Inventory & Stock" },
  { k: "expenses", l: "Expense Ledger" },
  { k: "cash_collection", l: "Cash Collection" },
];

const SNAPSHOT_TYPES = ["credit", "inventory"];

export function ReportsPanel() {
  const [type, setType] = useState("sales");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const isSnapshot = SNAPSHOT_TYPES.includes(type);
  const params = () => (isSnapshot ? {} : { start: start || undefined, end: end || undefined });

  const iso = (d) => format(d, "yyyy-MM-dd");
  const financialYear = () => {
    const now = new Date();
    const y = now.getFullYear();
    const fyStartYear = now.getMonth() >= 3 ? y : y - 1; // April = month index 3
    return { start: iso(new Date(fyStartYear, 3, 1)), end: iso(new Date(fyStartYear + 1, 2, 31)) };
  };
  const presets = [
    { k: "this_month", l: "This Month", range: () => ({ start: iso(startOfMonth(new Date())), end: iso(endOfMonth(new Date())) }) },
    { k: "last_month", l: "Last Month", range: () => { const d = subMonths(new Date(), 1); return { start: iso(startOfMonth(d)), end: iso(endOfMonth(d)) }; } },
    { k: "this_fy", l: "This FY", range: financialYear },
  ];
  const applyPreset = (p) => { const r = p.range(); setStart(r.start); setEnd(r.end); };

  const run = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/${type}`, { params: params() }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/${type}`, { params: { ...params(), format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${type}_report.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <>
      <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Analytics</p>
      <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">Reports & Exports</h1>
      <div className="bg-white border border-brand-line rounded-md p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5"><Label className="text-sm font-semibold">Report</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="border-brand-line" data-testid="report-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-sm font-semibold">Date Range</Label>
            <DateRangePicker start={start} end={end} disabled={isSnapshot} onChange={({ start, end }) => { setStart(start); setEnd(end); }} testid="report-range" />
          </div>
          <Button onClick={run} data-testid="run-report" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}</Button>
        </div>
        {isSnapshot ? (
          <p className="text-xs text-brand-muted mt-3" data-testid="snapshot-note">This is a point-in-time snapshot showing current values — the date range does not apply.</p>
        ) : (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-brand-muted uppercase tracking-wider mr-1">Quick range</span>
          {presets.map((p) => {
            const r = p.range();
            const activePreset = start === r.start && end === r.end;
            return (
              <button key={p.k} onClick={() => applyPreset(p)} data-testid={`preset-${p.k}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${activePreset ? "bg-brand-primary text-white border-brand-primary" : "border-brand-line text-brand-text hover:bg-brand-bg"}`}>
                {p.l}
              </button>
            );
          })}
        </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button onClick={() => download("pdf")} data-testid="export-pdf" variant="outline" className="rounded-sm border-brand-line gap-2"><FileText className="w-4 h-4" /> PDF</Button>
          <Button onClick={() => download("xlsx")} data-testid="export-xlsx" variant="outline" className="rounded-sm border-brand-line gap-2"><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          <Button onClick={() => download("csv")} data-testid="export-csv" variant="outline" className="rounded-sm border-brand-line gap-2"><FileDown className="w-4 h-4" /> CSV</Button>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto" data-testid="report-preview">
          <div className="px-5 py-3 border-b border-brand-line"><h3 className="font-semibold">{preview.title} <span className="text-brand-muted text-sm font-normal">· {preview.rows.length} rows</span></h3></div>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">{preview.columns.map((c, i) => <TableHead key={i} className="text-xs uppercase tracking-wider whitespace-nowrap">{c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {preview.rows.length === 0 && <TableRow><TableCell colSpan={preview.columns.length} className="text-center text-brand-muted py-8">No data for this range.</TableCell></TableRow>}
              {preview.rows.map((r, i) => (
                <TableRow key={i} className="hover:bg-brand-bg/50">{r.map((c, j) => <TableCell key={j} className="text-sm whitespace-nowrap">{String(c)}</TableCell>)}</TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
